
import { supabase } from '@/integrations/supabase/client';

export interface AdminUser {
  id: string;
  user_id: string;
  role: 'admin' | 'moderator';
  created_at: string;
  updated_at: string;
  created_by: string | null;
  user_name: string | null;
  user_email: string | null;
}

export interface AuditLogEntry {
  id: string;
  admin_user_id: string;
  action: string;
  target_user_id: string | null;
  details: any; // Changed from Record<string, any> | null to any to match Json type
  created_at: string;
}

// Validate if user exists before admin assignment
export const validateUserExists = async (userId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase.rpc('user_exists', {
      user_uuid: userId
    });

    if (error) {
      console.error('Error validating user existence:', error);
      return false;
    }

    return data === true;
  } catch (error) {
    console.error('Error validating user existence:', error);
    return false;
  }
};

// Get user by email for secure admin invitations
export const getUserByEmail = async (email: string) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id, full_name, email')
      .eq('email', email.toLowerCase().trim())
      .maybeSingle();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error finding user by email:', error);
    throw error;
  }
};

// Get admin users with enhanced security
export const getAdminUsers = async (): Promise<AdminUser[]> => {
  const { data: adminData, error } = await supabase
    .from('admin_users')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  if (!adminData) return [];

  // Enrich with user data using secure public profile function
  const enrichedAdmins = await Promise.all(
    adminData.map(async (admin) => {
      const { data: profileData } = await supabase.rpc('get_public_profile', {
        profile_user_id: admin.user_id
      });

      const profile = profileData?.[0];

      return {
        ...admin,
        user_name: profile?.full_name || null,
        user_email: null, // Don't expose email for security
      } as AdminUser;
    })
  );

  return enrichedAdmins;
};

// Add admin user with validation
export const addAdminUser = async (
  userId: string,
  role: 'admin' | 'moderator',
  userEmail?: string
) => {
  // Validate user exists first
  const userExists = await validateUserExists(userId);
  if (!userExists) {
    throw new Error('User does not exist. Please verify the user ID or email.');
  }

  const { data: currentUser } = await supabase.auth.getUser();
  if (!currentUser.user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('admin_users')
    .insert({
      user_id: userId,
      role,
      created_by: currentUser.user.id,
    })
    .select()
    .single();

  if (error) {
    if (error.code === '23505') { // Unique constraint violation
      throw new Error('User is already an admin or moderator');
    }
    throw error;
  }

  return data;
};

// Update admin user role
export const updateAdminUser = async (
  adminId: string,
  role: 'admin' | 'moderator'
) => {
  const { data, error } = await supabase
    .from('admin_users')
    .update({
      role,
      updated_at: new Date().toISOString(),
    })
    .eq('id', adminId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Remove admin user
export const removeAdminUser = async (adminId: string) => {
  const { error } = await supabase
    .from('admin_users')
    .delete()
    .eq('id', adminId);

  if (error) throw error;
};

// Get audit log entries
export const getAdminAuditLog = async (): Promise<AuditLogEntry[]> => {
  const { data, error } = await supabase
    .from('admin_audit_log')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(100);

  if (error) throw error;
  return data || [];
};

// Check current user role securely
export const getCurrentUserRole = async () => {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) return null;

  try {
    const { data, error } = await supabase.rpc('get_user_role', {
      user_uuid: user.user.id
    });

    if (error) {
      console.error('Error getting user role:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error getting user role:', error);
    return null;
  }
};
